import React, { useState, useCallback, useContext, useRef, useEffect } from "react";
import { Handle, Position, useNodeId } from "@xyflow/react";
import { UpdateBoundaryContext } from "./UpdateBoundaryContext";
import { portHandleClass } from "./PortHandle";
import { CONNECTOR_LABEL, SIGNAL_LEVEL_LABEL } from "../constants/signalLabels";

export default function SignalBoundaryNode({ data }) {
  const { boundary } = data;
  const nodeId = useNodeId();
  const updateBoundary = useContext(UpdateBoundaryContext);
  const isOutput = boundary.direction === "output";
  const handleId = `boundary-${boundary.id || "new"}`;

  const [editing, setEditing] = useState(false);
  const [editValue, setEditValue] = useState(boundary.label);
  const inputRef = useRef(null);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  const commitEdit = useCallback(() => {
    const trimmed = editValue.trim();
    if (trimmed && trimmed !== boundary.label && updateBoundary) {
      updateBoundary(nodeId, { label: trimmed });
    }
    setEditing(false);
  }, [editValue, boundary.label, updateBoundary, nodeId]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === "Enter") {
      commitEdit();
    } else if (e.key === "Escape") {
      setEditValue(boundary.label);
      setEditing(false);
    }
  }, [commitEdit, boundary.label]);

  const handleClassName = portHandleClass(boundary);

  const details = [];
  if (boundary.connector) details.push(CONNECTOR_LABEL[boundary.connector] || boundary.connector);
  if (boundary.signal_level) details.push(SIGNAL_LEVEL_LABEL[boundary.signal_level] || boundary.signal_level);
  if (boundary.phantom_power === "required") details.push("\u26A1 Phantom");

  return (
    <div className={`signal-boundary-node signal-boundary-node--${boundary.direction}`}>
      {isOutput && (
        <Handle
          type="target"
          position={Position.Left}
          id={handleId}
          className={handleClassName}
          role="button"
          aria-label="Signal boundary input"
        />
      )}
      <div className="signal-boundary-node-content">
        <div className="signal-boundary-node-direction">
          {isOutput ? "Output \u2192" : "\u2192 Input"}
        </div>
        {editing ? (
          <input
            ref={inputRef}
            className="signal-boundary-node-input"
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onBlur={commitEdit}
            onKeyDown={handleKeyDown}
          />
        ) : (
          <div
            className="signal-boundary-node-label"
            onDoubleClick={() => {
              setEditValue(boundary.label);
              setEditing(true);
            }}
            title="Double-click to edit"
          >
            {boundary.label}
          </div>
        )}
        {details.length > 0 && (
          <div className="signal-boundary-node-details">{details.join(" \u00b7 ")}</div>
        )}
      </div>
      {!isOutput && (
        <Handle
          type="source"
          position={Position.Right}
          id={handleId}
          className={handleClassName}
          role="button"
          aria-label="Signal boundary output"
        />
      )}
    </div>
  );
}
